import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem, RotateCcw, Sparkles } from 'lucide-react'
import confetti from 'canvas-confetti'
import audioService from '../../../services/audioService'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

const RoyaleWinnerCard = ({
  winner,
  winTicket,
  winAmount,
  winPercent,
  isCurrentUser,
  isParticipant,
  refundAmount = 0,
  skipEffects = false
}) => {
  if (!winner) return null

  const { t } = useTranslation()
  const isWinnerUser = Boolean(isCurrentUser || winner?.you)
  const isRoundParticipant = Boolean(isParticipant)
  // Тиммейт победителя: ставка вернулась, это НЕ проигрыш — ни текстом, ни звуком
  const refundValue = Number(refundAmount || 0)
  const isRefunded = !isWinnerUser && isRoundParticipant && refundValue > 0

  useEffect(() => {
    if (skipEffects) return undefined

    if (isRoundParticipant) {
      if (isWinnerUser) {
        hapticNotify('success')
        hapticImpact('heavy')
      } else if (isRefunded) {
        hapticImpact('light')
      } else {
        hapticNotify('error')
      }
    }

    if (isWinnerUser) {
      audioService.play('royaleWin')
    } else if (isRoundParticipant && !isRefunded) {
      audioService.play('upgraderFail')
    } else {
      audioService.play('upgraderWin')
    }

    if (!isWinnerUser) return undefined

    const duration = 3000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 }

    const randomInRange = (min, max) => Math.random() * (max - min) + min

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        clearInterval(interval)
        return
      }

      const particleCount = 50 * (timeLeft / duration)

      // Конфетти при победе
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
    }, 250)

    return () => clearInterval(interval)
  }, [isWinnerUser, isRoundParticipant, isRefunded, skipEffects])

  const formattedWin = Number(winAmount || 0).toLocaleString()
  const percentNumber = Number(winPercent)
  const formattedPercent = Number.isFinite(percentNumber)
    ? `${percentNumber.toFixed(2)}%`
    : '—'
  const percentToneClass = Number.isFinite(percentNumber) && percentNumber < 5
    ? 'bg-[#FFD166]/15 text-[#FFD166] border-[#FFD166]/40'
    : 'bg-accent-primary/15 text-accent-primary border-accent-primary/30'
  const statusLabel = isRoundParticipant
    ? isWinnerUser
      ? t('royale.winner.statusWin')
      : isRefunded
        ? t('royale.winner.statusRefund')
        : t('royale.winner.statusLose')
    : null
  const statusClassName = isWinnerUser
    ? 'border-accent-primary/60 bg-accent-primary/15 text-accent-primary'
    : isRefunded
      ? 'border-info/40 bg-info/10 text-info'
      : 'border-red-400/40 bg-red-500/10 text-red-200'

  return (
    <motion.div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#10161f] via-[#121a1c] to-[#0f1116] p-5 shadow-[0_0_24px_rgba(0,255,127,0.15)]"
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div className="absolute -top-8 -right-6 h-24 w-24 rounded-full bg-accent-primary/15 blur-2xl" />
      <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-[#4DDCFF]/10 blur-2xl" />

      <div className="relative z-10 flex flex-col items-center text-center gap-4">
        {statusLabel && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${statusClassName}`}>
            {statusLabel}
          </span>
        )}
        <div>
          {/* Аватар и имя победителя кликабельны: slug === null → не кликабелен */}
          <ProfileLink slug={winner.slug} className="mt-3 flex flex-col items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-accent-primary/40 blur-lg" />
              <img
                src={winner.ava || `https://ui-avatars.com/api/?name=${winner.name}&background=00FF7F&color=0A0A0F`}
                alt={winner.name}
                className="relative w-20 h-20 rounded-full object-cover border border-white/20"
              />
            </div>
            <div>
              <p className="text-white text-xl font-semibold">
                <TeamTag team={winner.team} /> <br />
                {winner.name}
              </p>
            </div>
          </ProfileLink>
        </div>
        <div className="flex flex-col items-center gap-1">
          <p className="text-xs text-text-secondary">{t('royale.winner.totalWin')}</p>
          <p className="inline-flex items-center gap-2 text-2xl font-bold text-white">
            <Gem className="w-5 h-5 text-accent-primary" />
            {formattedWin}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border ${percentToneClass}`}>
          {formattedPercent}
        </span>

        {/* Сколько вернулось тиммейту: кристальная часть падает на баланс, скины — в инвентарь */}
        {isRefunded && (
          <div className="inline-flex items-center gap-2 rounded-full border border-info/30 bg-info/10 px-3 py-1.5 text-info">
            <RotateCcw className="h-4 w-4" />
            <span className="text-sm font-bold tabular-nums">{refundValue.toLocaleString()}</span>
            <Gem className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default RoyaleWinnerCard
